import { ServerMessageSchema, type ClientMessage, type ServerMessage } from "@deckloom/core/protocol";

export type RoomListener = (msg: ServerMessage) => void;

/** Clock sync (a ping the room answers with its time) only matters for media sync. */
const CLOCK_SYNC_MS = 60_000;
/** Changes kept while the room cannot be reached, sent once it can. */
const OUTBOX_MAX = 500;

/** Messages worth sending late; the rest (media, pings) are stale by then. */
function queueable(msg: ClientMessage): boolean {
  return msg.type === "goto" || msg.type.startsWith("stroke.");
}

/**
 * A deck room, followed over its event stream (SSE, read from a POST so that proxies which hold
 * GET streams back let it through) and spoken to over HTTP (`POST …/room`, one message each).
 * Reconnects with backoff, keeps changes made while the room was out of reach and sends them once
 * back, and tracks the server clock offset when asked to.
 */
export class RoomClient {
  private abort: AbortController | null = null;
  private listeners = new Set<RoomListener>();
  private closed = false;
  private retry = 0;
  private timers: number[] = [];
  private outbox: ClientMessage[] = [];
  private clockSync = false;
  /** this tab, for undoing its own drawings as a viewer */
  private readonly clientId = crypto.randomUUID();
  /** serverTime - Date.now() estimate */
  clockOffset = 0;

  /** told when the room refuses the connection with a reason (a plan limit); the client keeps trying */
  onRefused: ((reason: string) => void) | null = null;

  constructor(private readonly deckId: string, private readonly token: string | null) {}

  private headers(json = false): Record<string, string> {
    return { ...(this.token ? { authorization: `Bearer ${this.token}` } : {}), "x-client-id": this.clientId, ...(json ? { "content-type": "application/json" } : {}) };
  }

  connect(): void {
    this.closed = false;
    void this.follow();
  }

  private async follow(): Promise<void> {
    const abort = new AbortController();
    this.abort = abort;
    let opened = false;
    try {
      const res = await fetch(`/api/decks/${this.deckId}/events`, { method: "POST", headers: this.headers(), signal: abort.signal });
      if (!res.ok || !res.body) {
        const reason = ((await res.json().catch(() => ({}))) as { error?: string }).error;
        if (res.status === 429 && reason) this.onRefused?.(reason);
        if (res.status === 404) return; // no such deck: nothing to come back to
        throw new Error(reason ?? `${res.status}`);
      }
      opened = true;
      this.retry = 0;
      this.flush();
      this.startClockSync();
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += value;
        let end: number;
        while ((end = buffer.indexOf("\n\n")) >= 0) {
          const data = buffer.slice(0, end).split("\n").filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("\n");
          buffer = buffer.slice(end + 2);
          if (data) this.receive(data);
        }
      }
    } catch {
      /* dropped: come back below */
    }
    this.stopTimers();
    if (this.closed || abort.signal.aborted) return;
    const delay = opened ? 500 : Math.min(10000, 500 * 2 ** this.retry++);
    window.setTimeout(() => void this.follow(), delay);
  }

  private receive(data: string): void {
    let raw: unknown;
    try { raw = JSON.parse(data); } catch { return; }
    const parsed = ServerMessageSchema.safeParse(raw);
    if (!parsed.success) return;
    const msg = parsed.data;
    if (msg.type === "state") this.clockOffset = msg.serverTime - Date.now();
    if (msg.type === "deck.deleted") this.close();
    for (const l of this.listeners) l(msg);
  }

  /** Turn periodic clock syncing on for decks with video or audio. */
  setClockSync(on: boolean): void {
    if (this.clockSync === on) return;
    this.clockSync = on;
    this.stopTimers();
    this.startClockSync();
  }

  private startClockSync(): void {
    if (!this.clockSync || this.closed) return;
    this.ping();
    this.timers.push(window.setInterval(() => this.ping(), CLOCK_SYNC_MS));
  }

  private stopTimers(): void {
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
  }

  private ping(): void {
    const t = Date.now();
    this.post({ type: "ping", t })
      .then((r) => {
        const parsed = ServerMessageSchema.safeParse(r);
        if (!parsed.success || parsed.data.type !== "pong") return;
        const now = Date.now();
        this.clockOffset = parsed.data.serverTime + (now - parsed.data.t) / 2 - now;
      })
      .catch(() => undefined);
  }

  private async post(msg: ClientMessage): Promise<unknown> {
    const res = await fetch(`/api/decks/${this.deckId}/room`, { method: "POST", headers: this.headers(true), body: JSON.stringify(msg), keepalive: true });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  }

  /** Send a message; one the room cannot be reached for is kept (if worth it) and sent once it can. */
  send(msg: ClientMessage): void {
    this.post(msg).catch((err: Error) => {
      // 4xx is an answer (not allowed, bad message): not worth repeating
      if (/^4\d\d$/.test(err.message) || !queueable(msg)) return;
      if (msg.type === "goto") this.outbox = this.outbox.filter((m) => m.type !== "goto");
      if (this.outbox.length < OUTBOX_MAX) this.outbox.push(msg);
    });
  }

  private flush(): void {
    const pending = this.outbox;
    this.outbox = [];
    for (const msg of pending) this.send(msg);
  }

  on(l: RoomListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  close(): void {
    this.closed = true;
    this.stopTimers();
    this.abort?.abort();
    this.abort = null;
  }
}
